import React, { useState } from "react";
import Button from "@mui/material/Button";
import { TextField } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addData, getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import Loader from "../../component/Loader";
import { useSnackbarContext } from "../../component/SnackbarContext";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import { formatCurrencyAmount } from "../../Utility/currencyDisplay";
import StreakConfetti from "../MonthlyPromotions/StreakConfetti";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": { borderRadius: 16, maxWidth: 420, width: "100%" },
  "& .MuiDialogContent-root": { padding: theme.spacing(3) },
}));

// What the player received. The API already converted every reward from USD into
// the player's own currency (fiat or crypto) and tells us which one it used, so
// these lines show real wallet amounts, not the admin's USD figures.
const rewardLines = (data) => {
  const currency = data?.currency;
  const lines = [];
  if (Number(data?.cashAmount) > 0) {
    lines.push(`${formatCurrencyAmount(data.cashAmount, currency)} Cash`);
  }
  if (Number(data?.bonusAmount) > 0) {
    lines.push(`${formatCurrencyAmount(data.bonusAmount, currency)} Bonus`);
  }
  if (Number(data?.freeSpins) > 0) {
    lines.push(`${Number(data.freeSpins)} Free Spins`);
  }
  return lines;
};

const RedeemPromo = ({ openPromoModal, closePromoModal }) => {
  const IsLoginData = useSelector((state) => state.auth.IsLoginData);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext();

  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [reward, setReward] = useState(null);             // cash / bonus / free-spin confirmation
  const [boostPackages, setBoostPackages] = useState([]); // BONUS_PERCENT packages
  const [boostPercent, setBoostPercent] = useState(0);

  const pink = theme.palette.custom.pink_bg;
  const text = theme.palette.custom.white_gray;
  const currency = IsLoginData?.currency;

  // Fire-and-forget: never await this before showing the result. Awaiting it dispatches a
  // Redux update that re-renders the layout, which would swallow the confirmation screen
  // (rewards credited, but the player sees no message).
  const refreshWallet = () => {
    getAllData(`${endPoints.api.GET_BY_USERID}/${IsLoginData?.idUser}`)
      .then((res) => dispatch(setLoginData({ user: res?.data, isAuthenticated: true })))
      .catch((e) => console.log(e));
  };

  // Keep only the packages this promo applies to (blank list = any package).
  const loadBoostPackages = async (csvIds) => {
    try {
      const res = await getAllData(`${endPoints.api.GET_PACKAGE}`);
      const all = Array.isArray(res) ? res : res?.data || [];
      if (!csvIds) return all;
      const ids = String(csvIds).split(",").map((s) => s.trim());
      return all.filter((p) => ids.includes(String(p.id)));
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const claim = async () => {
    setLoading(true);
    try {
      const response = await addData(`${endPoints.api.PROMO_REDEEM}`, { promoCode: code });
      setLoading(false);
      const body = response?.data;

      // No readable body = the call failed in transit. The promo may still have been applied,
      // so tell the player to check their balance instead of implying the code was bad.
      if (!body) {
        showErrorSnackbar("Couldn't confirm your claim. Please check your balance before trying again.");
        return;
      }
      if (body.status !== "success") {
        showErrorSnackbar(body.message || "Invalid promo code");
        return;
      }

      const data = body.data;
      setCode("");

      // BONUS_PERCENT: nothing credited yet — show the boosted package(s) to buy.
      if (data?.rewardType === "BONUS_PERCENT") {
        const pkgs = data?.packages?.length ? data.packages : await loadBoostPackages(data?.bonusPackageIds);
        setBoostPercent(Number(data?.boostPercent) || Number(pkgs?.[0]?.boostValue) || 0);
        setBoostPackages(pkgs);
        if (!pkgs.length) {
          closePromoModal();
          navigate("/buy-coins");
        }
        return;
      }

      // Credited (or a referral boost armed). Show the result FIRST (and always snackbar,
      // which is rendered outside this modal), then refresh the wallet in the background.
      setReward(data || {});
      const summary = rewardLines(data).join(" + ");
      showSuccessSnackbar(summary ? `You received ${summary}!` : body.message || "Promo claimed successfully!");
      refreshWallet();
    } catch (error) {
      setLoading(false);
      showErrorSnackbar("Couldn't confirm your claim. Please check your balance before trying again.");
      console.log("error", error);
    }
  };

  // promoPackageId, not packageId — the latter opens the email-campaign modal, which
  // resolves packages by a different column and reports ours as unavailable.
  const goBuyPackage = (pkg) => {
    closePromoModal();
    // Hand the package over directly — it is already loaded here, so buy-coins does
    // not have to look it up again.
    navigate(`/buy-coins?promoPackageId=${pkg.id}`, { state: { promoPackage: pkg } });
  };

  // purchase_price already arrives in the player's currency, so the boost is just
  // that same percentage of it.
  const boostPreview = (pkg) =>
    formatCurrencyAmount((Number(pkg.purchase_price) * boostPercent) / 100, pkg.currency || currency);

  const primaryBtn = {
    background: pink,
    color: text,
    fontWeight: 800,
    borderRadius: 10,
    "&:hover": { background: pink, opacity: 0.9 },
  };

  const celebrating = !!reward || boostPackages.length > 0;

  const renderBody = () => {
    // ---------- BONUS_PERCENT: boosted packages ----------
    if (boostPackages.length > 0) {
      return (
        <>
          <div style={{ fontSize: 40, lineHeight: 1 }}>⚡</div>
          <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 1 }}>{`+${boostPercent}% Bonus Activated`}</Typography>
          <Typography sx={{ fontSize: 14, opacity: 0.75, mt: 0.5, mb: 2 }}>
            Buy a package below to instantly get your bonus.
          </Typography>

          {boostPackages.map((pkg) => (
            <div
              key={pkg.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "14px 16px",
                marginBottom: 12,
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${pink}`,
              }}
            >
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {pkg.package_name || formatCurrencyAmount(pkg.purchase_price, pkg.currency || currency)}
                </div>
                {pkg.package_amount ? (
                  <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{pkg.package_amount}</div>
                ) : null}
                <div
                  style={{
                    display: "inline-block",
                    marginTop: 6,
                    padding: "2px 8px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    background: "rgba(46,204,113,0.15)",
                    color: "#2ecc71",
                  }}
                >
                  {`+ ${boostPreview(pkg)} bonus ⚡`}
                </div>
              </div>
              <Button onClick={() => goBuyPackage(pkg)} sx={{ ...primaryBtn, px: 2, whiteSpace: "nowrap" }}>
                {`Buy ${formatCurrencyAmount(pkg.purchase_price, pkg.currency || currency)}`}
              </Button>
            </div>
          ))}
        </>
      );
    }

    // ---------- reward confirmation ----------
    if (reward) {
      const lines = rewardLines(reward);
      return (
        <>
          <div style={{ fontSize: 40, lineHeight: 1 }}>🎉</div>
          <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 1 }}>Congratulations!</Typography>
          <Typography sx={{ fontSize: 14, opacity: 0.75, mt: 0.5, mb: 2 }}>You've claimed your promo reward.</Typography>

          <div
            style={{
              padding: "16px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${pink}`,
              marginBottom: 20,
            }}
          >
            {lines.length ? (
              lines.map((line) => (
                <div key={line} style={{ fontSize: 18, fontWeight: 800, color: "#2ecc71", padding: "4px 0" }}>
                  + {line}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {reward.message || "Your reward has been added to your balance."}
              </div>
            )}
          </div>

          <Button fullWidth onClick={closePromoModal} sx={{ ...primaryBtn, py: 1.2, fontSize: 16 }}>
            Awesome!
          </Button>
        </>
      );
    }

    // ---------- code entry ----------
    return (
      <>
        <div style={{ fontSize: 40, lineHeight: 1 }}>🎁</div>
        <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 1 }}>Promo Claim</Typography>
        <Typography sx={{ fontSize: 14, opacity: 0.75, mt: 0.5, mb: 2.5 }}>
          Enter your promo code to claim your reward.
        </Typography>

        <TextField
          fullWidth
          size="medium"
          placeholder="ENTER CODE"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && code && claim()}
          inputProps={{ style: { textAlign: "center", letterSpacing: 2, fontWeight: 700 } }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 10 } }}
        />

        <Button
          fullWidth
          onClick={claim}
          disabled={code === ""}
          sx={{
            ...primaryBtn,
            mt: 2.5,
            py: 1.2,
            fontSize: 16,
            "&.Mui-disabled": { background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.4)" },
          }}
        >
          Claim
        </Button>
      </>
    );
  };

  return (
    <BootstrapDialog onClose={closePromoModal} open={openPromoModal} className="first_modal">
      <IconButton
        onClick={closePromoModal}
        sx={{ position: "absolute", right: 10, top: 10, color: theme.palette.grey[500], zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          position: "relative",
          background: theme.palette.custom.black2_light2,
          color: text,
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        {celebrating && <StreakConfetti count={45} />}
        <div style={{ position: "relative", zIndex: 1 }}>
          {loading && <Loader loading={loading} />}
          {renderBody()}
        </div>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default RedeemPromo;
